import React, { useState, useEffect, useRef } from 'react';
import {
  IonTextarea,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText
} from '@ionic/react';
import { sendOutline, closeOutline } from 'ionicons/icons';
import { isOnline } from '../utils/offlineManager';
import { Storage } from '@capacitor/storage';
import './QuestionInput.css';

interface QuestionInputProps {
  isOpen: boolean;
  sessionId: string;
  onClose: () => void;
  onSubmit: (question: string) => Promise<boolean>;
  onError: (message: string) => void;
}

interface QuestionTimestamp {
  timestamp: number;
}

const MAX_QUESTIONS = 5;
const TIME_WINDOW_MS = 5 * 60 * 1000; // 5 minutos en milisegundos

const QuestionInput: React.FC<QuestionInputProps> = ({
  isOpen,
  sessionId,
  onClose,
  onSubmit,
  onError
}) => {
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remainingChars, setRemainingChars] = useState(500);
  const [recentQuestions, setRecentQuestions] = useState<QuestionTimestamp[]>([]);
  const [timeToWait, setTimeToWait] = useState(0);
  const textareaRef = useRef<HTMLIonTextareaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cargar historial de preguntas recientes al iniciar
  useEffect(() => {
    const loadRecentQuestions = async () => {
      try {
        const { value } = await Storage.get({ key: 'recentQuestions' });
        if (value) {
          const parsed = JSON.parse(value) as QuestionTimestamp[];
          // Filtrar solo las preguntas de los últimos 5 minutos
          const now = Date.now();
          const recent = parsed.filter(q => now - q.timestamp < TIME_WINDOW_MS);
          setRecentQuestions(recent);

          // Calcular tiempo de espera si es necesario
          if (recent.length >= MAX_QUESTIONS) {
            const oldestTimestamp = Math.min(...recent.map(q => q.timestamp));
            const timeToWaitMs = TIME_WINDOW_MS - (now - oldestTimestamp);
            if (timeToWaitMs > 0) {
              setTimeToWait(Math.ceil(timeToWaitMs / 1000));
            }
          }
        }
      } catch (error) {
        console.error('Error loading recent questions:', error);
      }
    };

    if (isOpen) {
      loadRecentQuestions();
      // Enfocar el textarea cuando se abre
      setTimeout(() => {
        textareaRef.current?.setFocus();
      }, 300);
    } else {
      setQuestion('');
      setRemainingChars(500);
    }
  }, [isOpen]);

  // Actualizar contador de caracteres restantes
  useEffect(() => {
    setRemainingChars(500 - question.length);
  }, [question]);

  // Actualizar contador de tiempo de espera
  useEffect(() => {
    if (timeToWait <= 0) return;

    const timer = setInterval(() => {
      setTimeToWait(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeToWait]);

  // Guardar una nueva pregunta en el historial
  const saveQuestionTimestamp = async () => {
    try {
      const now = Date.now();
      const newRecentQuestions = [...recentQuestions, { timestamp: now }];

      // Guardar solo las preguntas de los últimos 5 minutos
      const filtered = newRecentQuestions.filter(q => now - q.timestamp < TIME_WINDOW_MS);

      await Storage.set({
        key: 'recentQuestions',
        value: JSON.stringify(filtered)
      });

      setRecentQuestions(filtered);
    } catch (error) {
      console.error('Error saving question timestamp:', error);
    }
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!question.trim()) {
      onError('Por favor ingrese una pregunta');
      return;
    }

    if (question.length < 10) {
      onError('La pregunta debe tener al menos 10 caracteres');
      return;
    }

    // Verificar conexión
    if (!isOnline()) {
      onError('No hay conexión a internet. Su pregunta no puede ser enviada.');
      return;
    }

    // Verificar límite de preguntas
    if (recentQuestions.length >= MAX_QUESTIONS) {
      const now = Date.now();
      const oldestTimestamp = Math.min(...recentQuestions.map(q => q.timestamp));
      const timeToWaitMs = TIME_WINDOW_MS - (now - oldestTimestamp);

      if (timeToWaitMs > 0) {
        const minutes = Math.floor(timeToWaitMs / 60000);
        const seconds = Math.floor((timeToWaitMs % 60000) / 1000);
        onError(`Ha alcanzado el límite de preguntas. Por favor espere ${minutes}m ${seconds}s para hacer más preguntas.`);
        setTimeToWait(Math.ceil(timeToWaitMs / 1000));
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const success = await onSubmit(question);
      if (success) {
        await saveQuestionTimestamp();
        setQuestion('');
        onClose();
      } else {
        onError('Error al enviar la pregunta. Intente nuevamente.');
      }
    } catch (error) {
      onError('Error al enviar la pregunta. Intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`question-input-container ${isOpen ? 'open' : ''}`}
      style={{ zIndex: isOpen ? 1000 : -1 }} // Asegura que no interfiera cuando está cerrado
    >
      <div className="question-input-wrapper">
        <IonTextarea
          ref={textareaRef}
          value={question}
          onIonChange={e => setQuestion(e.detail.value!)}
          placeholder="Escriba su pregunta aquí..."
          rows={3}
          maxlength={500}
          className="question-input"
          disabled={isSubmitting || timeToWait > 0}
        ></IonTextarea>

        <div className="question-input-footer">
          <div className="character-count">
            <IonText color={remainingChars < 50 ? 'danger' : 'medium'}>
              {remainingChars}
            </IonText>
          </div>

          <div className="question-input-actions">
            {/* Botón de cerrar siempre visible */}
            <IonButton
              fill="clear"
              color="medium"
              onClick={onClose}
              disabled={isSubmitting}
              className="close-button"
            >
              <IonIcon icon={closeOutline} />
            </IonButton>

            {timeToWait > 0 ? (
              <div className="time-limit-warning">
                <IonText color="danger">
                  Espere {Math.floor(timeToWait / 60)}:{(timeToWait % 60).toString().padStart(2, '0')}
                </IonText>
              </div>
            ) : (
              <IonButton
                fill="solid"
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !question.trim() || question.length < 10 || timeToWait > 0}
                className="send-button"
              >
                {isSubmitting ? (
                  <IonSpinner name="dots" />
                ) : (
                  <IonIcon icon={sendOutline} />
                )}
              </IonButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionInput;
